import React, { useState, useEffect, useRef } from 'react';
import Slider, { Range } from 'rc-slider';
import { Grid, Row, Glyphicon, Label, Col, Button, FormControl, Modal } from 'react-bootstrap';
import { Knob } from 'react-rotary-knob';
import { Textfit } from 'react-textfit';
import './css/channel.css';

const FFTSIZE = 256;

export default function Channel({ stream, width, height, audioContext, audioStreamAvailable, channelStateChange, hangupCall, channelID, label, videoStream, name, mute, demoMode, reportObject, sendKeepalive, debug, onVideoMuteChange, handRaisedOrder, lowerRaisedHand }) {
    const [currentSliderValue, setCurrentSliderValue] = useState(1);
    const [videoMuted, setVideoMuted] = useState(false);
    const [lastSliderValue, setLastSliderValue] = useState(1);
    const [inputGainVolume, setInputGainVolume] = useState(33);
    const [instantVolume, setInstantVolume] = useState(0);
    const [slowVolume, setSlowVolume] = useState(0);
    const [channelOn, setChannelOn] = useState(false);
    const [bgFlashing, setBgFlashing] = useState(false);
    const [sliderBgFlashing, setSliderBgFlashing] = useState(false);
    const [utilityMeasurements, setUtilityMeasurements] = useState({
        utilityColWidth: 0,
        utilityColHeight: 0,
        utilityCubeMargin: 0,
        utilityVerticalOutsideMargin: 0,
        utilityHorizontalOutsideMargin: 0,
        utilityMaxHeight: 0
    });
    const [sliderMaxHeight, setSliderMaxHeight] = useState(0);
    const [channelButtonRowHeight, setChannelButtonRowHeight] = useState(0);
    const [bitrate, setBitrate] = useState(0);
    const [jitter, setJitter] = useState(0);
    const [iceRoute, setIceRoute] = useState('unknown');
    const [turnMbTotal, setTurnMbTotal] = useState('0.00');
    const [callStatus, setCallStatus] = useState('none');

    // Audio graph nodes
    const gainNodeRef = useRef({});
    const sliderVolumeRef = useRef({});
    const analyserRef = useRef({});
    const reqIdRef = useRef(0);
    const monitorIdRef = useRef(0);
    const timerIDRef = useRef(0);
    const timeDataRef = useRef([]);
    const bufferLengthRef = useRef(0);
    const maxLevelRef = useRef(0.0);
    const timeMaxLevelSetRef = useRef(0);
    const keepAliveIntervalIDRef = useRef(0);

    const containerRef = useRef(null);
    const gainCanvas = useRef(null);
    const videoRef = useRef(null);

    useEffect(() => {
        if (videoRef.current) videoRef.current.srcObject = videoStream || null;
    }, [videoStream]);

    const streamRef = useRef(stream);
    streamRef.current = stream;

    const liveRef = useRef({ sendKeepalive, name });
    liveRef.current.sendKeepalive = sendKeepalive;
    liveRef.current.name = name;

    // componentDidMount: demo mode init
    useEffect(() => {
        if (demoMode && name.length > 0) {
            setCallStatus('active');
            const multiplier = Math.random() / 10;
            setBitrate(parseFloat(40 * 1 + multiplier).toFixed(2));
            setJitter(parseFloat(1.08 * multiplier).toFixed(2));
            setInstantVolume(parseFloat(multiplier * 10).toFixed(2));
            setSlowVolume(parseFloat(multiplier * 10).toFixed(2));
            console.log('Channel %s multiplier %s', channelID, multiplier);
        }
    }, []);

    // CWRP stream
    const firstStreamRender = useRef(true);
    useEffect(() => {
        if (firstStreamRender.current) { firstStreamRender.current = false; return; }
        if (stream === null) {
            cancelAnimationFrame(reqIdRef.current);
            sliderVolumeRef.current.disconnect();
            gainNodeRef.current.disconnect();
            analyserRef.current.disconnect();
            gainNodeRef.current = {};
            sliderVolumeRef.current = {};
            analyserRef.current = {};
            timeDataRef.current = [];
            bufferLengthRef.current = 0;
            reqIdRef.current = 0;
            clearTimeout(monitorIdRef.current);
            clearTimeout(timerIDRef.current);
            clearInterval(keepAliveIntervalIDRef.current);

            setSlowVolume(0);
            setCurrentSliderValue(1);
            setInputGainVolume(33);
            setInstantVolume(0);
            setCallStatus('none');
            setSliderBgFlashing(false);
            setChannelOn(false);
        } else {
            setLastSliderValue(62);
            setCallStatus('active');
            setupGraph(stream);
            reqIdRef.current = requestAnimationFrame(monitorVolume);
            setBgFlashing(true);

            if (channelID === 'XX') {
                // auto-on for master channel
                setSliderBgFlashing(false);
                setChannelOn(true);
                sliderVolumeRef.current.gain.value = 1;
                channelStateChange(channelID, true);
            } else {
                setSliderBgFlashing(true);
                console.log('channel starting keepalive --------->', channelID);
                let count = 0;
                keepAliveIntervalIDRef.current = setInterval(() => {
                    liveRef.current.sendKeepalive(channelID, liveRef.current.name, count);
                    count++;
                }, 1000);
            }
        }
    }, [stream]);

    // CWRP width
    const firstWidthRender = useRef(true);
    useEffect(() => {
        if (firstWidthRender.current) { firstWidthRender.current = false; return; }
        calculateDimensions();
    }, [width]);

    // CWRP reportObject
    const firstReportRender = useRef(true);
    useEffect(() => {
        if (firstReportRender.current) { firstReportRender.current = false; return; }
        if (reportObject) createCharts();
    }, [reportObject]);

    function setupGraph(s) {
        gainNodeRef.current = audioContext.createGain();
        sliderVolumeRef.current = audioContext.createGain();
        sliderVolumeRef.current.gain.value = 0;

        analyserRef.current = audioContext.createAnalyser();
        analyserRef.current.minDecibels = -100;
        analyserRef.current.maxDecibels = 0;
        analyserRef.current.smoothingTimeConstant = 0.8;
        analyserRef.current.fftSize = FFTSIZE;
        bufferLengthRef.current = analyserRef.current.frequencyBinCount;
        timeDataRef.current = new Uint8Array(bufferLengthRef.current);

        s.connect(gainNodeRef.current);
        gainNodeRef.current.connect(analyserRef.current);
        analyserRef.current.connect(sliderVolumeRef.current);

        timeMaxLevelSetRef.current = Date.now();
        audioStreamAvailable(channelID, sliderVolumeRef.current);
    }

    function monitorVolume() {
        analyserRef.current.getByteFrequencyData(timeDataRef.current);
        let sum = 0.0;
        let max = 0.0;
        for (let freqBinI = 0; freqBinI < bufferLengthRef.current; freqBinI++) {
            sum += timeDataRef.current[freqBinI] * timeDataRef.current[freqBinI];
            if (timeDataRef.current[freqBinI] > max) {
                max = timeDataRef.current[freqBinI];
            }
        }

        const instant = parseFloat(max / 255).toFixed(2);

        if (instant > maxLevelRef.current) {
            maxLevelRef.current = instant;
            timeMaxLevelSetRef.current = Date.now();
        }
        if ((Date.now() - timeMaxLevelSetRef.current) > 2000) {
            maxLevelRef.current = instant;
            timeMaxLevelSetRef.current = Date.now();
        }

        setInstantVolume(instant);
        setSlowVolume(maxLevelRef.current);

        if (streamRef.current !== null) {
            reqIdRef.current = requestAnimationFrame(monitorVolume);
        }
    }

    function calculateDimensions() {
        const utilityMaxHeight = height * .25;
        const sliderMaxHeightVal = height - 38; // height - HEADER_H(28) - padding(10)
        const buttonMaxHeight = height * .15;
        const minGutter = 7;

        const maxUtilityColWidth = (width / 2) - (1 * minGutter);
        const maxUtilityColHeight = utilityMaxHeight / 2;
        let utilityColHeight = 0;
        let utilityColWidth = 0;
        let sideColMargin = 0;
        let topBotColMargin = 0;
        let vertOutsideMargin = 0;
        let horOutsideMargin = 0;

        if (maxUtilityColWidth > maxUtilityColHeight) {
            utilityColHeight = maxUtilityColHeight;
            utilityColWidth = maxUtilityColHeight;
            sideColMargin = (maxUtilityColWidth - utilityColWidth) / 4;
            vertOutsideMargin = (width - (2 * utilityColWidth)) / 4;
        } else {
            utilityColHeight = maxUtilityColWidth;
            utilityColWidth = maxUtilityColWidth;
            topBotColMargin = (maxUtilityColHeight - utilityColHeight) / 2;
            horOutsideMargin = (utilityMaxHeight - (2 * utilityColHeight)) / 2;
        }

        setUtilityMeasurements({
            utilityColWidth,
            utilityColHeight,
            sideColMargin,
            topBotColMargin,
            gutter: minGutter,
            horOutsideMargin,
            utilityMaxHeight
        });

        let channelButtonRowHeightVal = buttonMaxHeight;
        if (buttonMaxHeight > width) {
            channelButtonRowHeightVal = width;
        }
        setChannelButtonRowHeight(channelButtonRowHeightVal);
        setSliderMaxHeight(sliderMaxHeightVal);
    }

    function createCharts() {
        setBitrate(parseFloat(reportObject.bitrateIn).toFixed(2));
        setJitter(parseFloat(reportObject.jitter).toFixed(4));
        setIceRoute(reportObject.iceRoute || 'unknown');
        setTurnMbTotal(parseFloat(reportObject.turnMbTotal || 0).toFixed(2));
    }

    function hangupCallFn() {
        if (callStatus === 'active') {
            setCallStatus('bye');
            hangupCall(channelID, name);
        }
    }

    function buttonClick() {
        if (channelID === 'XX') {
            console.log('call status for XX:', callStatus);
        }
        if (callStatus !== 'active') return;

        const newChannelOn = !channelOn;
        let newVol = 1;
        if (sliderBgFlashing) {
            setSliderBgFlashing(false);
            setChannelOn(true);
        } else {
            if (newChannelOn) {
                setCurrentSliderValue(lastSliderValue);
                setChannelOn(true);
                newVol = Math.pow((lastSliderValue / 62), 2);
            } else {
                setLastSliderValue(currentSliderValue);
                setCurrentSliderValue(1);
                setChannelOn(false);
                newVol = 0;
            }
        }

        if (Number.isFinite(newVol)) {
            sliderVolumeRef.current.gain.value = newVol;
        } else {
            console.log('not finite #2', newVol);
        }
        channelStateChange(channelID, newChannelOn);
    }

    function sliderChange(sliderVal) {
        if (callStatus === 'active') {
            setCurrentSliderValue(sliderVal);
            setLastSliderValue(sliderVal);

            if (demoMode) return;

            const newVol = Math.pow((sliderVal / 62), 2);
            console.log(sliderVal, ': ', newVol);
            if (Number.isFinite(newVol)) {
                if (channelOn) {
                    sliderVolumeRef.current.gain.value = newVol;
                }
            } else {
                console.log('not finite #1', newVol);
            }
        }
    }

    function changeVolume(level) {
        const newGainValue = Math.pow((level / 33), 2);

        if (stream !== null) {
            if (Math.abs(inputGainVolume - level) > 20) return;
            if (level < 5) level = 5;
            if (level > 295) level = 295;
            setInputGainVolume(level);
            if (demoMode) return;
            gainNodeRef.current.gain.value = newGainValue;
        }
    }

    // canvas drawing in render (same as original class pattern)
    if (gainCanvas.current !== null) {
        const radius = utilityMeasurements.utilityColHeight / 2;
        const ctx = gainCanvas.current.getContext("2d");
        ctx.beginPath();
        ctx.fillStyle = 'white';
        ctx.moveTo(utilityMeasurements.utilityColHeight * .48, utilityMeasurements.utilityColHeight * .48);
        ctx.arc(utilityMeasurements.utilityColHeight * .47, utilityMeasurements.utilityColHeight * .47, utilityMeasurements.utilityColHeight * .48, 3.5 * Math.PI / 30, 6.5 * Math.PI / 30);
        ctx.closePath();
        ctx.fill();

        ctx.font = Math.round(radius * 0.55) + "px arial";
        ctx.textBaseline = "middle";
        ctx.textAlign = "start";
        ctx.fillStyle = 'white';
        ctx.fillText('-', radius * 1.5, 3);
        ctx.textAlign = "end";
        ctx.fillText('+', radius * .5, 5);

        ctx.lineWidth = radius * 0.1;
        ctx.strokeStyle = 'white';
        ctx.moveTo(radius, 0);
        ctx.lineTo(radius, 20);
        ctx.stroke();
    }

    const knobRowHeight = Math.round((width * 0.4) * 1.2);
    const sliderHeight = sliderMaxHeight;
    let sliderTrackWidth = Math.round(width * .07);
    if (sliderTrackWidth < 14) sliderTrackWidth = 14;
    const handleWidth = sliderTrackWidth * 4;

    let sliderVolumeHeight = (1 - instantVolume) * sliderHeight;
    let slowVolumeHeight = (1 - slowVolume) * sliderHeight;
    if (slowVolumeHeight < 0) slowVolumeHeight = 0;
    sliderTrackWidth = 3;

    const HEADER_H = 30;
    const outerHeight = height + "px";
    const innerHeight = (height - HEADER_H) + "px";

    let channelClass = "";
    if (bgFlashing && channelID !== 'XX') {
        channelClass = "rapidBlink";
    }

    let sliderBackgroundClass = "";
    if (sliderBgFlashing && channelID !== 'XX') {
        sliderBackgroundClass = "channelBlink";
    }

    let buttonBorderColor = "noCall";
    if (channelID === 'XX') {
        buttonBorderColor = channelOn ? "callActiveChannelOn" : "callActiveChannelOff";
    } else {
        if (sliderBgFlashing) {
            buttonBorderColor = "incomingCall";
        } else if (callStatus === 'active') {
            buttonBorderColor = channelOn ? "callActiveChannelOn" : "callOnHold";
        }
    }

    let jitterIcon = "staticImages/thumbsDown.svg";
    if (jitter < .01) {
        jitterIcon = "staticImages/thumbsUp.svg";
    } else if (jitter < .03) {
        jitterIcon = "staticImages/thumbsSoSo.svg";
    }

    const headerLabel = name.length > 0 ? name + (mute ? ' [muted]' : '') : (label ?? channelID);

    return (
        <div className="outer-container" style={{ height: outerHeight, width: "100%", position: 'relative' }} ref={containerRef}>
            {/* Single-line header: channel number or caller name */}
            <div style={{ height: HEADER_H + "px", display: 'flex', alignItems: 'center', padding: '0 6px', overflow: 'hidden', color: 'white', fontSize: '14px' }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{headerLabel}</span>
                {handRaisedOrder > 0 && (
                    <span className="raisedHandBadge"
                        onClick={e => {
                            e.stopPropagation();
                            if (lowerRaisedHand) lowerRaisedHand(name);
                        }}
                        title="Lower raised hand">
                        HAND #{handRaisedOrder}
                    </span>
                )}
                {iceRoute === 'turn' && callStatus === 'active' && (
                    <span className="turnRouteBadge" title={`TURN relay ${turnMbTotal}MB · ${bitrate}k in`}>TURN</span>
                )}
                {/* Jitter */}
                <img src={jitterIcon}
                    height="20" width="20"
                    style={{ flexShrink: 0, display: channelID !== 'XX' && callStatus === 'active' ? 'block' : 'none' }} />
                {/* Hangup */}
                <img src="staticImages/noun_X_2500111.svg"
                    height="18" width="18"
                    style={{ flexShrink: 0, marginLeft: '5px', cursor: "pointer", display: channelID !== 'XX' && callStatus === 'active' ? 'block' : 'none' }}
                    onClick={hangupCallFn} />
            </div>
            {/* 16:9 visualization area */}
            <div style={{ color: "yellow", height: innerHeight, width: "100%", borderWidth: '1px', borderStyle: channelID === 'XX' ? 'dotted' : 'solid', padding: "5px", margin: "0px", position: 'relative', overflow: 'hidden' }}
                onClick={buttonClick}
                className={buttonBorderColor}>
                {/* Caller video — preserve full frame, letterboxing non-16:9 streams */}
                <video ref={videoRef} autoPlay playsInline muted
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000', display: videoStream && !videoMuted ? 'block' : 'none', zIndex: 0 }} />
                {/* Camera mute overlay — only shown when a video stream is present */}
                {videoStream && (
                    <img
                        src={videoMuted ? 'staticImages/webcam-off.svg' : 'staticImages/webcam-on.svg'}
                        height="22" width="22"
                        onClick={e => {
                            e.stopPropagation();
                            const next = !videoMuted;
                            setVideoMuted(next);
                            if (onVideoMuteChange) onVideoMuteChange(next);
                        }}
                        style={{ position: 'absolute', bottom: '6px', right: '6px', zIndex: 3,
                                 cursor: 'pointer', opacity: videoMuted ? 1 : 0.45 }}
                    />
                )}
                <div style={{ width: sliderTrackWidth, height: sliderVolumeHeight, backgroundColor: "#333333", borderRadius: '0px', position: "absolute", left: "5px", top: "5px", zIndex: 2 }}
                    className={sliderBackgroundClass} />
                <div style={{ width: sliderTrackWidth, height: sliderHeight, backgroundImage: 'linear-gradient(red 0%, red 12%, yellow 12%, yellow 37%, gray 37%, gray 100%)', position: "absolute", left: "5px", top: '5px', borderRadius: '0px', zIndex: 1 }} />
                <div style={{ width: 0, height: 0, display: callStatus !== 'active' ? 'none' : 'block', borderTop: "2px solid transparent", borderBottom: "2px solid transparent", borderLeft: "2px solid white", position: "absolute", left: "10px", top: slowVolumeHeight - 1, zIndex: 100 }} />
            </div>
        </div>
    );
}
